import type { Schema } from '@/shared/api/contract';

/**
 * The payroll (contract v10.53, ADR 0047 of the back). Straight from the contract,
 * `cubas.domain.schemas.payroll` of the back. Only whoever has `can_send_payroll` sees
 * any of it; the API answers 403 to anybody else, also to whoever administers.
 */
export type PayrollStatus = Schema<'PayrollStatusRead'>;
export type PayrollBatch = Schema<'PayrollBatchRead'>;
export type PayrollBatchDetail = Schema<'PayrollBatchDetail'>;
export type PayrollBatchState = Schema<'PayrollBatchStatus'>;
export type PayrollSlip = Schema<'PayrollSlipRead'>;
export type PayrollNote = Schema<'PayrollNote'>;
export type PayrollTest = Schema<'PayrollTestRead'>;
export type PayrollSendState = Schema<'PayrollSendStatus'>;
export type PayrollMatch = Schema<'PayrollMatch'>;
export type PayrollEmployee = Schema<'PayrollEmployeeRead'>;
export type PayrollEmployeeWrite = Schema<'PayrollEmployeeWrite'>;
export type PayrollEmployeeUpdate = Schema<'PayrollEmployeeUpdate'>;
export type PayrollEmployeeCreated = Schema<'PayrollEmployeeCreated'>;
export type PayrollPasswordIssued = Schema<'PayrollPasswordIssued'>;
export type PayrollImportResult = Schema<'PayrollImportResult'>;

/** What the worker has in its hands: the screen asks again every few seconds. */
export const WORKING: ReadonlySet<PayrollBatchState> = new Set([
  'detecting',
  'preparing',
  'sending',
]);

/** Ready or sent: the payslips are there, to look at, assign and send. */
export function hasSlips(batch: Pick<PayrollBatch, 'status'>): boolean {
  return batch.status === 'ready' || batch.status === 'sent' || batch.status === 'sending';
}

/**
 * The payslips that still need a person: not left out, not sent. What the list calls
 * «Por revisar».
 */
export function unassignedCount(batch: PayrollBatch): number {
  return Math.max(0, (batch.slips ?? 0) - (batch.excluded ?? 0) - (batch.assigned ?? 0));
}

/**
 * The refused and unconfirmed payslips somebody still has to chase. The back counts every
 * one, also those marked «No enviar» afterwards; with the payslips at hand (a month's
 * screen) those are left out, so a month does not stay red for them.
 */
export function withOpenFailures(batch: PayrollBatchDetail): PayrollBatchDetail {
  if (!batch.items) return batch;
  const open = batch.items.filter((slip) => !slip.excluded);
  return {
    ...batch,
    refused: open.filter((slip) => slip.send_status === 'refused').length,
    unknown: open.filter((slip) => slip.send_status === 'unknown').length,
  };
}

/** The line of a batch in the list and at the top of its screen, without the month. */
export function stateLabel(batch: PayrollBatch): string {
  switch (batch.status) {
    case 'detecting':
      return 'Comprobando';
    case 'not_payroll':
      return 'No parece nómina';
    case 'preparing':
      return 'Separándolas';
    case 'needs_password':
      return 'Falta la contraseña';
    case 'ready':
      return unassignedCount(batch) > 0 ? 'Por revisar' : 'Listas para enviar';
    case 'failed':
      return 'No se pudieron preparar';
    case 'sending':
      return 'Enviándose';
    case 'sent':
      return (batch.refused ?? 0) > 0 || (batch.unknown ?? 0) > 0
        ? 'Enviadas, con fallos'
        : 'Enviadas';
    case 'discarded':
      return 'Descartadas';
  }
}

export type Tone = 'neutral' | 'ok' | 'warn' | 'danger' | 'info' | 'accent';

export function stateTone(batch: PayrollBatch): Tone {
  switch (batch.status) {
    case 'ready':
      return unassignedCount(batch) > 0 ? 'warn' : 'info';
    case 'needs_password':
      return 'warn';
    case 'failed':
      return 'danger';
    case 'sent':
      return (batch.refused ?? 0) > 0 || (batch.unknown ?? 0) > 0 ? 'danger' : 'ok';
    case 'detecting':
    case 'preparing':
    case 'sending':
      return 'info';
    case 'not_payroll':
    case 'discarded':
      return 'neutral';
  }
}

/**
 * NOM-01…09 (`cubas.payroll` of the back), as the line over the sentence the back sends
 * in `error`. NOM-05, NOM-06 and NOM-07 are not the file's: «Volver a intentarlo» asks
 * again (the back says so in `can_retry`).
 */
export function failureTitle(code: string | null | undefined): string {
  switch (code) {
    case 'NOM-01':
      return 'El fichero no se puede abrir';
    case 'NOM-02':
      return 'El fichero es demasiado grande';
    case 'NOM-03':
      return 'Dentro no hay ningún PDF';
    case 'NOM-04':
      return 'No hay ninguna página que leer';
    case 'NOM-06':
      return 'El almacén de las nóminas no respondió';
    case 'NOM-07':
      return 'No se pudo pedir que se preparen';
    case 'NOM-08':
      return 'El servidor de correo cortó el envío a medias';
    case 'NOM-09':
      return 'No se puede enviar desde aquí';
    default:
      return 'No se pudieron preparar';
  }
}

const MONTH = new Intl.DateTimeFormat('es-ES', { month: 'long', year: 'numeric', timeZone: 'UTC' });

/** `2026-10` → «octubre de 2026». Null for a batch without a month yet. */
export function periodText(period: string | null | undefined): string | null {
  const match = period?.match(/^(\d{4})-(\d{2})$/);
  if (!match) return null;
  return MONTH.format(new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1)));
}

/** «Nóminas de octubre de 2026», or «Nóminas» while there is no month. */
export function batchTitle(batch: Pick<PayrollBatch, 'period'>): string {
  const month = periodText(batch.period);
  return month ? `Nóminas de ${month}` : 'Nóminas';
}

export const MATCH_LABELS: Record<PayrollMatch, string> = {
  nif: 'por el NIF',
  name: 'por el nombre',
  manual: 'a mano',
};

/** What can be uploaded by hand: what the payroll firm sends, as it sends it. */
export const PAYROLL_ACCEPT = '.zip,.pdf,application/zip,application/pdf';
export const EMPLOYEES_ACCEPT =
  '.xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv';

/** «1 nómina», «21 nóminas». */
export function payslips(count: number): string {
  return count === 1 ? '1 nómina' : `${count} nóminas`;
}

export function people(count: number): string {
  return count === 1 ? '1 persona' : `${count} personas`;
}
